import { Link } from 'react-router-dom';
import { usePublicConfig } from '../PublicShell';
import { LegalLayout, Section, List, Table } from './LegalLayout';

export default function Privacy() {
  const c = usePublicConfig();
  const company = c.company.name;
  const product = c.company.product;

  return (
    <LegalLayout
      title="Privacy policy"
      updated="11 September 2026"
      intro={
        <>
          {product} is the software; {company} is the company that builds and operates it, and
          is the organisation named in this policy. It explains what {company} does with
          personal data in connection with {product}, and it covers two different
          relationships — it matters which one you are in.
        </>
      }
    >
      <Section title="Two roles, and which applies to you">
        <p>
          <strong className="text-ink">If you are a {product} customer</strong> — a landlord or
          agency with an account — {company} is the <em>controller</em> of your account data:
          your name, email, phone number and billing details.
        </p>
        <p>
          <strong className="text-ink">If you are a tenant</strong> whose details appear in a
          landlord&rsquo;s {product} account, that landlord is the controller. {company} is only
          the <em>processor</em>, acting on their instructions. We do not decide what is held
          about you or how long it is kept.
        </p>
        <p>
          That distinction decides who you ask. Tenants should contact their landlord or agency
          first; if you cannot reach them, write to us at{' '}
          <a href={`mailto:${c.contact.privacy}`}>{c.contact.privacy}</a> and we will help you
          reach the right person. The terms on which we act as a processor are in the{' '}
          <Link to="/data-processing">data processing agreement</Link>.
        </p>
      </Section>

      <Section title="What we hold about customers">
        <Table
          head={['Data', 'Why', 'Lawful basis']}
          rows={[
            ['Name, email, phone', 'Running your account and contacting you about it', 'Contract'],
            ['Password (hashed, never stored in readable form)', 'Signing you in', 'Contract'],
            ['Organisation name, address, currency', 'Configuring your account', 'Contract'],
            ['Sign-in times and actions taken in the app', 'Security, and the audit trail your account relies on', 'Legitimate interests'],
            ['Billing records', 'Taking payment and meeting tax obligations', 'Contract, and legal obligation'],
            ['Support correspondence', 'Answering you', 'Legitimate interests'],
          ]}
        />
      </Section>

      <Section title="What is held on behalf of landlords">
        <p>
          When a landlord uses {product}, the following may be stored in their account. We
          process it only to provide the service to them:
        </p>
        <List
          items={[
            'Tenant names, phone numbers, email addresses and dates of birth',
            'Identification details and uploaded ID documents',
            'Employment and emergency contact details',
            'Tenancy terms, rent, deposits and payment history',
            'Uploaded documents — contracts, receipts, photographs',
            'Records of text messages and emails sent, and their delivery status',
            'Whether an email we sent was opened, where the landlord’s deployment has that switched on — see below',
            'Maintenance requests and their resolution',
            'Tenancy agreements, and the name, date and time a tenant typed when signing one',
            'What a tenant says about a standing order they have set up with their own bank',
            'Lines from a bank statement the landlord has chosen to import — a date, an amount, a description and a reference',
          ]}
        />
        <p>
          Imported bank statement lines are the landlord&rsquo;s own statement, uploaded by
          them as a file. {company} has no connection to anybody&rsquo;s bank account, cannot
          see one, and cannot move money. Only credits are kept; what a landlord spends is
          their business and is discarded on import.
        </p>
        <p>
          Uploaded identification documents can contain special category data — for example a
          photograph revealing ethnicity, or a document showing nationality. Landlords are
          responsible for deciding whether to upload such documents and for having a lawful
          basis to do so. We do not inspect, index or analyse their contents.
        </p>
      </Section>

      <Section title="Knowing whether an email was opened">
        <p>
          Email can be sent with a small tracking image that reports back when a
          mail client loads it. {company} keeps this <strong className="text-ink">switched off by
          default</strong>. Where it is switched on, we record the first time a message was
          opened, how many times the image was fetched, and whether a link in it was followed.
        </p>
        <p>
          It is a poor measurement and we would rather say so than imply otherwise: some mail
          clients fetch the image the moment a message arrives, before anybody has read
          anything, and others never fetch it at all. It is not evidence that a person read
          a message, and nothing in {product} treats it as such — a message read in the tenant
          portal is recorded separately, because that one actually happened.
        </p>
        <p>
          Delivery and failure are recorded whether or not tracking is on. Those are facts
          about whether mail arrived rather than about what anybody did, and they are what
          answers &ldquo;they say they never got it&rdquo;.
        </p>
      </Section>

      <Section title="What we never do">
        <List
          items={[
            'We do not sell personal data, and never have.',
            'We do not use customer or tenant data to train machine learning models.',
            'We do not use tracking or advertising cookies. See the cookies page.',
            'We do not read your documents or your tenants’ documents except where you ask us to for support, and then only with your agreement at the time.',
          ]}
        />
      </Section>

      <Section title="Suppliers">
        <p>
          We use a small number of suppliers to run the service — for hosting, storage,
          text messaging, email and the optional live chat on our own help page. Each is bound by a written contract that permits them to process data only
          on our instructions. Customers can see the current list, and are told before it
          changes, under the <Link to="/data-processing">data processing agreement</Link>.
        </p>
      </Section>

      <Section title="Where data is held">
        <p>
          Data is held on servers in the United Kingdom and the European Economic Area where
          possible. Some suppliers process data in the United States; where they do, transfers
          are covered by the UK International Data Transfer Addendum or by the supplier&rsquo;s
          certification under the UK extension to the EU&ndash;US Data Privacy Framework.
        </p>
      </Section>

      <Section title="How long we keep it">
        <List
          items={[
            <>
              <strong className="text-ink">While your account is open</strong> — for as long as
              you keep it, because it is the record you are relying on.
            </>,
            <>
              <strong className="text-ink">When you delete your organisation</strong> — records
              are removed immediately. Uploaded documents are moved to a recovery area and
              destroyed within 30 days, so a mistake is not irreversible on the day it is made.
            </>,
            <>
              <strong className="text-ink">Backups</strong> — deleted data may persist in
              backups for up to 90 days before being overwritten.
            </>,
            <>
              <strong className="text-ink">Billing records</strong> — kept for six years, as UK
              tax law requires.
            </>,
          ]}
        />
      </Section>

      <Section title="Your rights">
        <p>
          Under UK GDPR you have the right to access your data, to have it corrected or erased,
          to restrict or object to processing, and to receive it in a portable form. You can
          also withdraw consent where consent is the basis we rely on.
        </p>
        <p>
          To exercise any of these, write to{' '}
          <a href={`mailto:${c.contact.privacy}`}>{c.contact.privacy}</a>. We will respond within
          one month. If you are a tenant, please contact your landlord first — see the top of
          this page.
        </p>
        <p>
          If you are unhappy with how we have handled your data you can complain to the
          Information Commissioner&rsquo;s Office at{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noreferrer noopener">ico.org.uk</a>,
          or by calling 0303 123 1113. We would rather you told us first so we can put it right.
        </p>
      </Section>

      <Section title="Security">
        <p>
          Passwords are stored only as salted, iterated hashes and cannot be read back. Sessions
          are signed and expire. Uploaded documents are stored privately and served only through
          the application&rsquo;s own permission checks — never as a public link. Tenants see
          only what their landlord has explicitly shared with them. Access to production systems
          is limited to those who need it.
        </p>
        <p>
          No system is perfectly secure. If we discover a breach affecting your data we will
          tell you, and the ICO where the law requires it, without undue delay.
        </p>
      </Section>

      <Section title="Changes">
        <p>
          If we change this policy in a way that materially affects you, we will email account
          owners at least 30 days beforehand. The date at the top always reflects the current
          version.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          {company}{c.company.number ? `, company number ${c.company.number}` : ''}.
        </p>
        {c.company.address && <p>{c.company.address}</p>}
        <p>
          Data protection enquiries:{' '}
          <a href={`mailto:${c.contact.privacy}`}>{c.contact.privacy}</a>
        </p>
      </Section>
    </LegalLayout>
  );
}
